import { useState, useEffect, useCallback } from 'react';
import { urls } from '../urls'
import dayjs from 'dayjs';
import { v4 as uuid } from 'uuid'
import { useCustomModalStore } from 'state';
import { useToastAlert } from 'common/alerts';

const DELIVERY_STATUS_REFRESH_DELAY_MS = 7000;

export const useEmailActivity = (regId: string) => {
    const adjudicationURL = urls.ADJUDICATION;
    const [emailActivity, setEmailActivity] = useState<any>([]);
    const [emailCount, setEmailCount] = useState<number>(0);
    const [loading, setLoading] = useState(false);
    const setOpenCustomModal = useCustomModalStore(state => state.setOpenCustomModal)
    const { notifySuccess, notifyError } = useToastAlert();

    const adjudicationUrl = `${process.env.NODE_ENV === 'production' ? adjudicationURL : ""}/adjudication`

    const fetchListOfEmailActivity = useCallback(async (controller?: AbortController) => {
        const signal = controller?.signal;

        try {
            const listOfEmailActivity = await (await fetch(`${adjudicationUrl}/secure-doc-table/${regId}?order=descending&page=1&count=100`, {
                method: 'GET',
                credentials: "include",
                signal
            })).json();


            const transformData = listOfEmailActivity.secureDoc.map((data) => {
                const expiredDate = typeof data.expireDate === 'string'
                    ? (data.expireDate.startsWith('"') ? JSON.parse(data.expireDate) : data.expireDate)
                    : '';

                const mostRecentDate = typeof data.resubmitDate === 'string'
                    ? (data.resubmitDate.startsWith('"') ? JSON.parse(data.resubmitDate) : data.resubmitDate)
                    : '';


                const formattedDate = (date: any) => dayjs
                    .utc(date)
                    .local()
                    .format("MM/DD/YYYY hh:mm:ss A");


                return {
                    key: uuid(),
                    id: data.regId,
                    recipient: data.recipientName,
                    recipientEmail: data.emailAddress,
                    date: formattedDate(mostRecentDate),
                    deliveryStatus: data.latestStatus,
                    resend: "resend",
                    expiredDate: expiredDate,
                    resubmitCount: data.resubmitCount
                }
            })
            setEmailActivity(transformData)
        } catch (err) {
            console.error("error fetching applicant's documents:", err);
        }
    }, [adjudicationUrl, regId]);

    const fetchEmailActivityCount = useCallback(async (controller?: AbortController) => {
        const signal = controller?.signal;

        try {
            const emailActivityCount = await (await fetch(`${adjudicationUrl}/secure-doc-table/count/${regId}`, {
                method: 'GET',
                credentials: "include",
                signal
            })).json();

            setEmailCount(emailActivityCount.count);
        } catch (err) {
            console.error("error fetching applicant's documents:", err);
        }
    }, [adjudicationUrl, regId]);

    const resendEmail = async (id, email) => {
        let resp;
        let body = {
            "listOfApplicants": [
                {
                    "regId": id,
                    "emailAddress": email
                }
            ]
        }
        setOpenCustomModal(false);
        try {
            resp = (await fetch(`${adjudicationUrl}/resubmit-applicant-pdfs`, {
                method: 'POST',
                credentials: "include",
                body: JSON.stringify(body),
            }))

            if (resp.ok) {
                setTimeout(async () => {
                    setLoading(true);
                    await fetchListOfEmailActivity()
                    setLoading(false);
                }, DELIVERY_STATUS_REFRESH_DELAY_MS)
                notifySuccess("Email sent.", "Your email has been sent. You can review the delivery status in the Secure Email Activity Log.");
            } else {
                const respJSON = await resp.json();
                notifyError("Error", respJSON.message);
            }
        } catch (err) {
            console.error("error fetching applicant's documents:", err);
        }
        return resp;
    }

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        Promise.all([
            fetchListOfEmailActivity(controller),
            fetchEmailActivityCount(controller)
        ]).finally(() => setLoading(false));

        return () => controller.abort()
    }, [fetchListOfEmailActivity, fetchEmailActivityCount])


    return { emailActivity, emailCount, resendEmail, loading }
}
